import React, { useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { usePosts } from '../hooks/usePosts';
import { useSearch } from '../hooks/useSearch';
import { PostList } from '../components/post/PostList';
import { Button } from '../components/common/Button';
import { SearchBar } from '../components/search/SearchBar';
import { SearchFilters } from '../components/search/SearchFilters';
import type { SearchFiltersValue } from '../components/search/SearchFilters';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/useLanguage';
import { HomeSlideshow } from '../components/home/HomeSlideshow';
import type { SearchParams } from '../types/search';

const hasActiveFilters = (filters: SearchFiltersValue): boolean =>
  !!filters.sightingType ||
  (filters.location?.lat !== undefined && filters.location?.lon !== undefined) ||
  !!filters.dateRange?.fromDate ||
  !!filters.dateRange?.toDate;

const buildSearchParams = (
  query: string,
  filters: SearchFiltersValue,
  page: number,
  size: number
): SearchParams => ({
  query,
  page,
  size,
  sightingType: filters.sightingType,
  lat: filters.location?.lat,
  lon: filters.location?.lon,
  radiusKm: filters.location?.lat !== undefined && filters.location?.lon !== undefined
    ? filters.location?.radiusKm
    : undefined,
  fromDate: filters.dateRange?.fromDate,
  toDate: filters.dateRange?.toDate,
});

export const HomePage: React.FC = () => {
  const { t } = useLanguage();
  const { posts, loading, error, pagination, fetchPosts } = usePosts();
  const {
    results: searchResults,
    loading: searchLoading,
    error: searchError,
    pagination: searchPagination,
    query: searchQuery,
    search,
    clearSearch,
  } = useSearch();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [filters, setFilters] = useState<SearchFiltersValue>({});
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Refs for the "Steckbriefe suchen" jump button.
  const searchSectionRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filtersActive = hasActiveFilters(filters);

  // Fetch posts when not in search mode and no filters are active.
  useEffect(() => {
    if (!isSearchMode && !filtersActive) {
      try {
        fetchPosts({ page: currentPage, size: pagination.size });
      } catch (err) {
        console.error('Error fetching posts:', err);
      }
    }
  }, [currentPage, fetchPosts, isSearchMode, filtersActive, pagination.size]);

  /**
   * Run a search if either the query or any filter is set; otherwise clear
   * search mode so the regular post feed is shown.
   */
  const performSearch = useCallback(
    (
      query: string,
      activeFilters: SearchFiltersValue = filters,
      sizeOverride?: number,
      pageOverride?: number
    ) => {
      const trimmed = query.trim();
      const haveFilters = hasActiveFilters(activeFilters);

      if (!trimmed && !haveFilters) {
        setIsSearchMode(false);
        clearSearch();
        return;
      }

      setIsSearchMode(true);
      search(
        buildSearchParams(
          trimmed,
          activeFilters,
          pageOverride ?? currentPage,
          sizeOverride ?? searchPagination.size
        )
      );
    },
    [search, clearSearch, currentPage, searchPagination.size, filters]
  );

  const handleSearch = useCallback(
    (query: string) => {
      performSearch(query, filters);
    },
    [performSearch, filters]
  );

  const handleFiltersChange = useCallback(
    (next: SearchFiltersValue) => {
      setFilters(next);
      // Re-run search with the current text query and the new filters. If
      // both are empty, performSearch will exit search mode for us.
      performSearch(searchQuery && searchQuery.trim() ? searchQuery : '', next);
    },
    [performSearch, searchQuery]
  );

  const handleJumpToSearch = useCallback(() => {
    const section = searchSectionRef.current;
    if (!section) return;

    // Account for the sticky toolbar (which already sits below the global
    // sticky header). Using its bounding rect lets us subtract the real
    // pixel height regardless of language, viewport, or zoom.
    const stickyToolbar = document.querySelector('.home-page-sticky-toolbar');
    const stickyBottom = stickyToolbar
      ? stickyToolbar.getBoundingClientRect().bottom
      : 0;
    const sectionTop = section.getBoundingClientRect().top;
    const breathingRoom = 12; // small gap so the field isn't flush against the toolbar
    const delta = sectionTop - stickyBottom - breathingRoom;

    if (Math.abs(delta) > 1) {
      window.scrollBy({ top: delta, behavior: 'smooth' });
    }

    // Wait for the scroll to settle before focusing so iOS Safari does not
    // snap back to where the focused input would otherwise land.
    setTimeout(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    }, 350);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      if (isSearchMode) {
        performSearch(searchQuery, filters, undefined, page);
      }
    },
    [isSearchMode, searchQuery, filters, performSearch]
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setCurrentPage(0);
      if (isSearchMode) {
        performSearch(searchQuery, filters, size, 0);
      } else {
        fetchPosts({ page: 0, size });
      }
    },
    [isSearchMode, searchQuery, filters, performSearch, fetchPosts]
  );

  const handleRefreshList = useCallback(() => {
    if (isSearchMode) {
      performSearch(searchQuery, filters);
    } else {
      fetchPosts({ page: currentPage, size: pagination.size });
    }
  }, [isSearchMode, searchQuery, filters, performSearch, fetchPosts, currentPage, pagination.size]);

  // Convert search results into the Post shape PostList expects.
  const displayPosts = isSearchMode
    ? searchResults.map((result) => ({
        id: result.id,
        authorId: result.authorId,
        authorUsername: result.authorUsername,
        title: result.title || '',
        content: result.content || result.excerpt || '',
        tags: result.tags || [],
        createdAt: result.createdAt,
        updatedAt: result.createdAt,
        edited: result.metadata?.edited === true || false,
        deleted: false,
        sightingType: result.sightingType,
      }))
    : posts;

  const displayLoading = isSearchMode ? searchLoading : loading;
  const displayError = isSearchMode ? searchError : error;
  const displayPagination = isSearchMode
    ? {
        page: searchPagination.page,
        size: searchPagination.size,
        totalElements: searchPagination.totalCount,
        totalPages: searchPagination.totalPages,
        first: searchPagination.page === 0,
        last: searchPagination.page >= searchPagination.totalPages - 1,
      }
    : pagination;

  // Show "M of N matches" only while a search is active and not in flight,
  // so we don't flash stale numbers between requests.
  const matchesIndicator = (() => {
    if (!isSearchMode || searchLoading) return null;
    const matches = searchPagination.totalCount;
    const total = pagination.totalElements;
    const key: 'search.matches' | 'search.matchesOne' | 'search.noMatches' =
      matches === 0 ? 'search.noMatches' : matches === 1 ? 'search.matchesOne' : 'search.matches';
    return t(key)
      .replace('{matches}', String(matches))
      .replace('{total}', String(total));
  })();

  return (
    <div className="home-page">
      <div className="home-page-sticky-toolbar">
        <Button
          type="button"
          variant="outline"
          className="home-page-create-steckbrief-button"
          onClick={() => navigate('/posts/new')}
        >
          <svg
            className="home-page-create-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden={true}
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('post.createSteckbrief')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="home-page-browse-steckbriefe-button"
          onClick={handleJumpToSearch}
        >
          <svg
            className="home-page-create-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden={true}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          {t('post.browseSteckbriefe')}
        </Button>
      </div>

      <div className="home-page-hero">
        <p className="home-page-hero-tagline-lead">{t('home.heroTaglineLead')}</p>
        <p className="home-page-hero-tagline-sub">{t('home.heroTaglineSub')}</p>
        <p className="home-page-hero-text">{t('home.heroBody1')}</p>
        <p className="home-page-hero-headline">{t('home.heroHeadline')}</p>
        <h1 className="home-page-hero-brand">{t('home.heroBrand')}</h1>
      </div>

      <a
        className="home-page-promo-banner-link"
        href="https://crime-club.at/crime-club-singles-and-social/i-think-its-you/#gewinnspiel"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          src="/Banner-ITI.jpg"
          alt="I Think It's You Gewinnspiel"
          className="home-page-promo-banner"
          loading="lazy"
          decoding="async"
        />
      </a>

      <HomeSlideshow />

      <div className="home-page-search" ref={searchSectionRef}>
        <div className={clsx('search-window', filtersExpanded && 'search-window--expanded')}>
          <SearchBar
            onSearch={handleSearch}
            initialValue={isSearchMode ? searchQuery : ''}
            placeholder={t('search.placeholder')}
            isLoading={isSearchMode ? searchLoading : false}
            inputRef={searchInputRef}
          />
          <SearchFilters
            value={filters}
            onChange={handleFiltersChange}
            expanded={filtersExpanded}
            onToggle={() => setFiltersExpanded((open) => !open)}
          />
        </div>
        {matchesIndicator && (
          <p className="home-page-search-indicator" aria-live="polite">
            {matchesIndicator}
          </p>
        )}
      </div>

      <h2 className="home-page-search-results-heading">
        {t(isSearchMode ? 'search.resultsHeading' : 'home.feedAllHeading')}
      </h2>

      <PostList
        posts={displayPosts}
        loading={displayLoading}
        error={displayError}
        pagination={displayPagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onRefreshList={handleRefreshList}
        showComments
      />
    </div>
  );
};
